import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceRankingTable = ({ rankings }) => {
  const getMethodIcon = (methodType) => {
    const icons = {
      'VA Transfer': 'Building2',
      'QRIS': 'QrCode',
      'OVO': 'Wallet',
      'DANA': 'Wallet',
      'GoPay': 'Wallet',
      'Card': 'CreditCard'
    };
    return icons?.[methodType] || 'DollarSign';
  };

  const getMethodColor = (methodType) => {
    const colors = {
      'VA Transfer': 'bg-blue-500',
      'QRIS': 'bg-purple-500',
      'OVO': 'bg-indigo-500',
      'DANA': 'bg-cyan-500',
      'GoPay': 'bg-green-500',
      'Card': 'bg-orange-500'
    };
    return colors?.[methodType] || 'bg-gray-500';
  };

  const getRankBadge = (rank) => {
    const badges = {
      1: { icon: 'Trophy', color: 'bg-yellow-500' },
      2: { icon: 'Medal', color: 'bg-gray-400' },
      3: { icon: 'Award', color: 'bg-orange-600' }
    };
    return badges?.[rank] || { icon: 'Hash', color: 'bg-muted' };
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-warning';
    return 'text-error';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
            Performance Rankings
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground mt-1 md:mt-2">
            Overall performance scores
          </p>
        </div>
        <Icon name="BarChart3" size={20} className="text-muted-foreground md:w-6 md:h-6" />
      </div>
      <div className="space-y-3 md:space-y-4">
        {rankings?.map((item) => {
          const badge = getRankBadge(item?.rank);
          return (
            <div
              key={item?.method}
              className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-lg bg-muted/50 hover:bg-muted transition-all duration-250"
            >
              <div
                className={`w-8 h-8 md:w-10 md:h-10 rounded-lg ${badge?.color} flex items-center justify-center flex-shrink-0`}
              >
                <Icon name={badge?.icon} size={16} color="white" className="md:w-5 md:h-5" />
              </div>
              <div
                className={`w-8 h-8 md:w-10 md:h-10 rounded-lg ${getMethodColor(
                  item?.method
                )} flex items-center justify-center flex-shrink-0`}
              >
                <Icon
                  name={getMethodIcon(item?.method)}
                  size={16}
                  color="white"
                  className="md:w-5 md:h-5"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-semibold text-foreground truncate">
                  {item?.method}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {item?.transactions?.toLocaleString('id-ID')} transactions
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p
                  className={`text-base md:text-lg lg:text-xl font-bold ${getScoreColor(
                    item?.score
                  )}`}
                >
                  {item?.score}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {item?.revenue}%
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          <div className="text-center">
            <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
              Top Performer
            </p>
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">
              {rankings?.[0]?.method}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
              Avg Score
            </p>
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">
              {(rankings?.reduce((sum, r) => sum + r?.score, 0) / rankings?.length)?.toFixed(1)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
              Total Volume
            </p>
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">
              {rankings?.reduce((sum, r) => sum + r?.transactions, 0)?.toLocaleString('id-ID')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceRankingTable;